import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import React from 'react';
import {getWidthAndHeightInPixels, PosterTypeUnits} from '@Libraries/poster-object.library';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {ControlledSlider} from '@Components/controlled-slider';
import {noop} from '@Utils/general.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {BaseSizeDropdownSelector} from '@Components/download-panel/components/base-size-dropdown-selector/base-size-dropdown-selector';
import {BaseSizeDropdown} from '@Components/download-panel/components/base-size-dropdown/base-size-dropdown';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import {GifDesignSizePopup} from '@Components/download-panel/components/gif-design-size-popup/gif-design-size-popup';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

const GIF_MAX_PIXEL_VALUE = 2000;
const GIF_MIN_PIXEL_VALUE = 50;

interface DownloadGifDesignSizeOptionProps {
  onSlideCallback?(): void;
  onSlideEndedCallback?(): void;
}

export function DownloadGifDesignSizeOption({onSlideCallback = noop, onSlideEndedCallback = noop}: DownloadGifDesignSizeOptionProps): ReactElement {
  const {scale, setScale, defaultOptions, posterObject, selectedDisplaySizeOption, setSelectedDisplaySizeOption} = useDownloadPanelData();
  const initialRender = useRef(true);
  const [min, setMin] = useState(0.1);
  const [max, setMax] = useState(4);
  const [doShowOriginalValue, setDoShowOriginalValue] = useState(true);
  const isSliderBeingDragged = useAppSelector((state) => {
    return state.downloadDialog.isSliderBeingDragged;
  });
  const dispatch = useAppDispatch();

  const onClickListItem = (id: SizeDropdownOptions, isClickFromModal = false): void => {
    setSelectedDisplaySizeOption(id);
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        setScale(1);
        break;
      case SizeDropdownOptions.LARGE: {
        const {width} = getLargeWidthAndHeight();
        const scaleFactorLarge = width / getWidthAndHeightInPixels(posterObject).width;
        setScale(scaleFactorLarge);
        break;
      }
      case SizeDropdownOptions.SMALL: {
        const {width: smallWidth} = getSmallWidthAndHeight();
        const scaleFactorSmall = smallWidth / getWidthAndHeightInPixels(posterObject).width;
        setScale(scaleFactorSmall);
        break;
      }
      case SizeDropdownOptions.CUSTOM:
      default:
        return;
    }

    if (isClickFromModal) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const getSliderForGIF = (): ReactElement => {
    return (
      <div className="spacing-m-t-2">
        <ControlledSlider key={`${min}-${max}`} marks={[1]} step={0.01} min={min} max={max} value={scale} onSlide={onSlideGifScale} onSlideEnded={onSlideEnded} />
      </div>
    );
  };

  const modalContent = (
    <GifDesignSizePopup
      getSliderForGIF={getSliderForGIF}
      onClickListItem={(id): void => {
        onClickListItem(id, true);
      }}
      doShowOriginalValue={doShowOriginalValue}
      scale={scale}
      posterObject={posterObject}
      selectedDisplaySizeOption={selectedDisplaySizeOption}
      setSelectedDisplaySizeOption={setSelectedDisplaySizeOption}
    />
  );
  useUpdateMoreOptionsModal(modalContent, [scale, selectedDisplaySizeOption, posterObject, doShowOriginalValue]);

  const getMinMaxForGIF = (): {min: number; max: number} => {
    const {width: pixelWidth, height: pixelHeight} = getWidthAndHeightInPixels(posterObject);

    return {
      min: GIF_MIN_PIXEL_VALUE / Math.min(pixelWidth, pixelHeight),
      max: GIF_MAX_PIXEL_VALUE / Math.max(pixelWidth, pixelHeight),
    };
  };

  const setInitialScale = (value: number): void => {
    if (defaultOptions?.scale && initialRender.current) {
      return;
    }

    initialRender.current = false;
    setScale(value);
  };

  useEffect(() => {
    const {min, max} = getMinMaxForGIF();
    setMin(Math.max(min, 0.01));
    setMax(max);
    setInitialScale(Math.min(max, 1));

    if (max < 1) {
      setSelectedDisplaySizeOption(SizeDropdownOptions.LARGE);
      setDoShowOriginalValue(false);
    } else {
      setDoShowOriginalValue(true);
    }
    return;
  }, [posterObject.type]);

  const onSlideGifScale = (value: number): void => {
    if (value > max || value < min) {
      return;
    }
    const dp = 2;
    const roundedValue = Number(value.toFixed(dp));
    setScale(roundedValue);
    onSlideCallback();
  };

  const onSlideEnded = (): void => {
    onSlideEndedCallback();
  };

  const getPopupForDropdown = (): ReactElement => {
    return (
      <GifDesignSizePopup
        getSliderForGIF={getSliderForGIF}
        onClickListItem={onClickListItem}
        doShowOriginalValue={doShowOriginalValue}
        scale={scale}
        posterObject={posterObject}
        selectedDisplaySizeOption={selectedDisplaySizeOption}
        setSelectedDisplaySizeOption={setSelectedDisplaySizeOption}
      />
    );
  };

  const getUnitsForDisplay = (): PosterTypeUnits => {
    return PosterTypeUnits.PX;
  };

  const getLargeWidthAndHeight = (): {width: number; height: number} => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    const maxDimension = Math.max(convertedWidth, convertedHeight);
    const threshold = 1920;

    const scaleFactor = threshold / maxDimension;
    return {
      width: Math.round(convertedWidth * scaleFactor),
      height: Math.round(convertedHeight * scaleFactor),
    };
  };

  const getSmallWidthAndHeight = (): {width: number; height: number} => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    const minDimension = Math.min(convertedWidth, convertedHeight);
    const threshold = 540;

    const scaleFactor = threshold / minDimension;
    return {
      width: Math.round(convertedWidth * scaleFactor),
      height: Math.round(convertedHeight * scaleFactor),
    };
  };

  const isCustomOptionSelected = (): boolean => {
    return selectedDisplaySizeOption === SizeDropdownOptions.CUSTOM;
  };

  const getSelectorTextForDropdown = (id: SizeDropdownOptions, forcefullyShowDimensionsWithCustom = false): string => {
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        return `${window.i18next.t('pmwjs_original')} · ${getWidthAndHeightInPixels(posterObject).width} x ${getWidthAndHeightInPixels(posterObject).height}${getUnitsForDisplay()}`;
      case SizeDropdownOptions.SMALL:
        return `${window.i18next.t('pmwjs_small')} · ${getSmallWidthAndHeight().width} x ${getSmallWidthAndHeight().height}${getUnitsForDisplay()}`;
      case SizeDropdownOptions.LARGE:
        return `${window.i18next.t('pmwjs_large')} · ${getLargeWidthAndHeight().width} x ${getLargeWidthAndHeight().height}${getUnitsForDisplay()}`;
      case SizeDropdownOptions.CUSTOM:
        return isCustomOptionSelected() || forcefullyShowDimensionsWithCustom
          ? ` ${window.i18next.t('pmwjs_custom')} · ${parseFloat((getWidthAndHeightInPixels(posterObject).width * scale).toFixed(2))} x ${parseFloat((getWidthAndHeightInPixels(posterObject).height * scale).toFixed(2))}${getUnitsForDisplay()}`
          : window.i18next.t('pmwjs_custom');
      default:
        return '';
    }
  };

  const getSelectorForDropdown = (): ReactElement => {
    return (
      <div>
        <BaseSizeDropdownSelector text={getSelectorTextForDropdown(selectedDisplaySizeOption)} />
      </div>
    );
  };

  const getDropdownOption = (): ReactElement => {
    return (
      <BaseSizeDropdown
        modalTitle={window.i18next.t('pmwjs_size')}
        popup={getPopupForDropdown()}
        modalContent={modalContent}
        selector={getSelectorForDropdown()}
        doesCloseOnOuterClick={!isSliderBeingDragged}
      />
    );
  };

  return getDropdownOption();
}
